/**
 * Return Protection — UI Bindings
 * --------------------------------
 * Listens to the controller's `return-protection:refreshed` events and
 * updates the footer, banner, and line-item tags in both the cart page and
 * the cart drawer.
 *
 * Also intercepts the checkout-button submission so it adds the protection
 * variant before navigating (rather than relying on it being in the cart).
 */
(function () {
  'use strict';

  function formatMoney(cents) {
    if (window.theme && window.theme.Currency && window.theme.Currency.formatMoney) {
      return window.theme.Currency.formatMoney(cents, window.theme.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Tri-state per-item exclusion check used by the line-item tagger.
   * Returns true only for 'clearance' — hat-shaping items are handled by the
   * kill switch path in handleRefresh, which forces hasEligible to false and
   * suppresses the tag application entirely.
   */
  function isItemExcluded(productHandle) {
    var map = window.__ReturnProtectionTagMap || {};
    if (!Object.prototype.hasOwnProperty.call(map, productHandle)) {
      return null; // unknown — controller will hydrate
    }
    var rawTags = map[productHandle] || [];
    var normalized = rawTags.map(function (t) {
      return String(t || '').trim().toLowerCase();
    });
    return normalized.indexOf('clearance') !== -1;
  }

  // Last-known cart eligibility state, populated by handleRefresh.
  var lastHasEligible = false;

  // -------------------------------------------------------------------------
  // DOM updates
  // -------------------------------------------------------------------------

  /**
   * Update the footer state across all rendered instances (cart page + drawer).
   *
   * Three visual states:
   *   PROTECTED:    tier row visible with price; checkout label "Continue with
   *                 Checkout+"; opt-out link visible.
   *   DEFAULT:      tier row hidden; checkout label "Check out"; opt-out hidden.
   *                 (Used when cart has no eligible items — excluded-only or empty.)
   */
  function updateFooters(tier, hasEligible) {
    var showProtection = !!tier && hasEligible;

    document.querySelectorAll('[data-return-protection]').forEach(function (footer) {
      var row = footer.querySelector('[data-rp-row]');
      var priceEl = footer.querySelector('[data-rp-price]');
      var optoutBtn = footer.querySelector('[data-rp-optout]');
      var labelProtected = footer.querySelector('[data-rp-checkout-label-protected]');
      var labelDefault = footer.querySelector('[data-rp-checkout-label-default]');
      var checkoutBtn = footer.querySelector('[data-rp-checkout]');

      if (row) row.hidden = !showProtection;
      if (optoutBtn) optoutBtn.hidden = !showProtection;

      if (showProtection) {
        if (priceEl) priceEl.textContent = formatMoney(tier.price);
        if (labelProtected) labelProtected.hidden = false;
        if (labelDefault) labelDefault.hidden = true;
      } else {
        if (labelProtected) labelProtected.hidden = true;
        if (labelDefault) labelDefault.hidden = false;
      }

      // Track on the button itself which mode it's in — used by the click
      // interceptor to know whether to call checkoutWithProtection() or let
      // the normal form submission happen.
      if (checkoutBtn) {
        checkoutBtn.setAttribute('data-rp-mode', showProtection ? 'protected' : 'default');
      }
    });
  }

  function updateExclusionNotice(showBanner) {
    document.querySelectorAll('[data-rp-exclusion-notice]').forEach(function (banner) {
      banner.hidden = !showBanner;
    });
  }

  /**
   * Extract variant ID and product handle from an Impulse cart line element.
   * Impulse 9.0 line format:
   *   <div class="cart__item" data-key="{variantId}:{lineItemKey}">
   *     <a href="/products/{handle}?variant=...">
   */
  function parseLineItem(el) {
    var variantId = null;
    var productHandle = null;

    var key = el.getAttribute('data-key');
    if (key) variantId = key.split(':')[0];

    if (!variantId) {
      variantId = el.getAttribute('data-variant-id')
        || el.getAttribute('data-cart-item-variant-id')
        || el.getAttribute('data-id');
    }

    if (!variantId) {
      var qtyInput = el.querySelector('input[data-id]');
      if (qtyInput) {
        var did = qtyInput.getAttribute('data-id') || '';
        variantId = did.split(':')[0];
      }
    }

    var link = el.querySelector('a[href*="/products/"]');
    if (link) {
      var href = link.getAttribute('href') || '';
      var match = href.match(/\/products\/([^/?#]+)/);
      if (match) productHandle = match[1];
    }

    return { variantId: variantId, productHandle: productHandle };
  }

  /**
   * Mark excluded line items with the "Final Sale" tag (mixed carts only),
   * and any lingering protection variant lines with the hide attribute.
   *
   * Note: with the new architecture, protection should never be in the cart
   * during normal browsing. But during the brief window between page load
   * and the controller's reconcile-strip, a stale protection variant might
   * be visible. The data-rp-protection-line attribute + CSS rule hides it.
   */
  function markCartLineItems(hasEligible) {
    var TIER_IDS = (window.ReturnProtection && window.ReturnProtection.getTiers)
      ? window.ReturnProtection.getTiers().map(function (t) { return String(t.id); })
      : [];

    var items = document.querySelectorAll(
      '.cart__item, [data-cart-item], .ajaxcart__product, .ajaxcart__row'
    );

    items.forEach(function (el) {
      var parsed = parseLineItem(el);

      // Hide any lingering protection variant lines
      if (parsed.variantId && TIER_IDS.indexOf(String(parsed.variantId)) !== -1) {
        if (!el.hasAttribute('data-rp-protection-line')) {
          el.setAttribute('data-rp-protection-line', '');
        }
        return;
      }

      var isExcluded = parsed.productHandle && isItemExcluded(parsed.productHandle) === true;
      var existingTag = el.querySelector('.rp-excluded-tag');
      // Only show the Final Sale tag in mixed carts
      var shouldShowTag = isExcluded && hasEligible;

      if (shouldShowTag && !existingTag) {
        var tag = document.createElement('span');
        tag.className = 'rp-excluded-tag';
        tag.textContent = 'Final Sale';

        var target = el.querySelector('.cart__item-title')
          || el.querySelector('.cart__item-details')
          || el.querySelector('.cart__item-meta')
          || el.querySelector('.ajaxcart__product-meta')
          || el;
        target.appendChild(tag);
      } else if (!shouldShowTag && existingTag) {
        existingTag.remove();
      }
    });
  }

  function markCartLineItemsWithState() {
    markCartLineItems(lastHasEligible);
  }

  // -------------------------------------------------------------------------
  // Event handling
  // -------------------------------------------------------------------------

  function handleRefresh(e) {
    var detail = e && e.detail || {};
    var tier = detail.tier;
    var hasEligible = !!detail.hasEligibleItems;
    var hasExcluded = !!detail.hasExcludedItems;
    var killSwitchActive = !!detail.killSwitchActive;

    // Kill switch wins over everything. When active, no RP UI shows anywhere:
    // no footer protection mode, no banner, no Final Sale tags on line items.
    // We do this by zeroing out the relevant inputs to the downstream updaters.
    if (killSwitchActive) {
      tier = null;
      hasEligible = false;
      hasExcluded = false;
    }

    lastHasEligible = hasEligible;

    var showBanner = hasEligible && hasExcluded;

    updateFooters(tier, hasEligible);
    updateExclusionNotice(showBanner);
    markCartLineItems(hasEligible);
  }

  /**
   * Capture-phase listener on the checkout button.
   * If we're in "protected" mode, intercept the form submission entirely:
   * add the protection variant, then navigate to /checkout.
   * If we're in "default" mode, let the normal form submission proceed.
   */
  function wireCheckoutButton() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-rp-checkout]');
      if (!btn) return;
      var mode = btn.getAttribute('data-rp-mode');
      if (mode !== 'protected') return; // let the form submit normally

      // Intercept — we'll handle the navigation
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();

      // Respect a terms checkbox if it's present and required
      var form = btn.closest('form');
      if (form) {
        var termsRequired = btn.getAttribute('data-terms-required') === 'true';
        var termsCheckbox = form.querySelector('.cart__terms-checkbox');
        if (termsRequired && termsCheckbox && !termsCheckbox.checked) {
          // Let Impulse's own terms validation handle this — re-fire the click
          // without our interception by triggering submit on the form.
          form.requestSubmit ? form.requestSubmit(btn) : form.submit();
          return;
        }
      }

      // Disable the button so the user can't double-click
      btn.disabled = true;
      // Visually indicate work-in-progress on the visible label
      var labelProtected = btn.querySelector('[data-rp-checkout-label-protected]');
      if (labelProtected) labelProtected.textContent = 'Adding protection…';

      if (window.ReturnProtection && window.ReturnProtection.checkoutWithProtection) {
        window.ReturnProtection.checkoutWithProtection();
      } else {
        // Fallback if controller hasn't loaded yet — direct navigate
        window.location.href = '/checkout';
      }
    }, true); // capture phase
  }

  /**
   * Capture-phase listener on the opt-out link.
   */
  function wireOptOut() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-rp-optout]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();

      btn.disabled = true;
      btn.textContent = 'Continuing to checkout…';

      if (window.ReturnProtection && window.ReturnProtection.checkoutWithoutProtection) {
        window.ReturnProtection.checkoutWithoutProtection();
      } else {
        window.location.href = '/checkout';
      }
    }, true); // capture phase
  }

  /**
   * Watch the cart product container for DOM mutations. Re-marks line items
   * whenever Impulse re-renders the drawer/page contents.
   */
  function installObserver() {
    if (typeof MutationObserver === 'undefined') return;

    var containers = document.querySelectorAll('[data-products]');
    if (!containers.length) return;

    var pending = false;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      requestAnimationFrame(function () {
        pending = false;
        markCartLineItemsWithState();
      });
    });

    containers.forEach(function (container) {
      observer.observe(container, { childList: true, subtree: true });
    });
  }

  function init() {
    document.addEventListener('return-protection:refreshed', handleRefresh);

    // Re-mark items whenever the cart re-renders
    [
      'ajaxCart.afterCartLoad',
      'cart:updated',
      'cart:build'
    ].forEach(function (evt) {
      document.addEventListener(evt, function () {
        setTimeout(markCartLineItemsWithState, 50);
      });
    });

    wireCheckoutButton();
    wireOptOut();

    // Initial pass for cart page (server-rendered)
    markCartLineItemsWithState();

    installObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();